import React, { memo, useMemo } from 'react';
import {
  ActionButton,
  Flex,
  Tooltip,
  TooltipTrigger
} from '@adobe/react-spectrum';
import ExpandIcon from 'images/expand-icon.svg';
import CollapseIcon from 'images/collapse-icon.svg';
import { DAY_WIDTH_PX } from '../utils/monthlyLayoutConstants';
import { getMonthSectionWidthPx } from '../utils/getVisibleDayColumns';
import type { MonthDescriptor } from '../utils/types';
import { DayHeaderRow } from './DayHeaderRow';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthSectionProps {
  month: MonthDescriptor;
  isExpanded: boolean;
  isCompact: boolean;
  onToggle: (monthKey: string) => void;
  widthPx?: number;
}

export const MonthSection = memo(
  ({ month, isExpanded, isCompact, onToggle, widthPx }: MonthSectionProps) => {
    const sectionWidthPx = useMemo(
      () =>
        widthPx ??
        getMonthSectionWidthPx(
          month.daysInMonth,
          isExpanded,
          isCompact,
          DAY_WIDTH_PX
        ),
      [month.daysInMonth, isExpanded, isCompact, widthPx]
    );

    return (
      <Flex
        data-testid={`monthly-month-section-${month.key}`}
        direction="column"
        UNSAFE_className={styles.monthSection}
        UNSAFE_style={{
          width: `${sectionWidthPx}px`,
          minWidth: `${sectionWidthPx}px`,
          gap: '10px'
        }}
      >
        <Flex alignItems="center" UNSAFE_className={styles.monthHeading}>
          <span className={styles.monthTitle}>{month.label}</span>

          <TooltipTrigger placement="top">
            <ActionButton
              isQuiet
              onPress={() => onToggle(month.key)}
              UNSAFE_className={styles.monthExpandButton}
            >
              <img
                className={styles.expandIcon}
                src={isExpanded ? ExpandIcon : CollapseIcon}
                alt={isExpanded ? 'Collapse Month' : 'Expand Month'}
                data-testid={`monthly-month-expand-${month.key}`}
              />
            </ActionButton>
            <Tooltip>{isExpanded ? 'Collapse Month' : 'Expand Month'}</Tooltip>
          </TooltipTrigger>
        </Flex>

        <DayHeaderRow
          month={month}
          isExpanded={isExpanded}
          isCompact={isCompact}
        />
      </Flex>
    );
  }
);
